import usePaddle from '@/features/subscription/hooks/usePaddle';
import useAuth from '@/hooks/useAuth';
import { AvailablePaymentMethod } from '@paddle/paddle-js';
import { useRef, useState } from 'react';
import { usePayment } from '@/features/subscription/hooks/usePayment';
import { useAppSelector } from '@/hooks/redux';
import { RootState } from '@/store';
import { useCreateTransaction } from '@/features/subscription/hooks/useCreateTransaction';
import { getCheckoutCustomerAddress } from '@/features/subscription/utils/paddle/paddlePostalCode';
import { useCheckoutPrewarm } from '@/features/subscription/contexts/CheckoutPrewarm';
import {
  checkoutVisibilityStore,
  CheckoutOwnerId,
} from '@/features/subscription/utils/checkout/checkoutVisibilityStore';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';

const RETAINED_TRANSACTION_TTL_MS = 30 * 60 * 1000;

const useCheckout = () => {
  const paddle = usePaddle();
  const { user } = useAuth();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState<boolean>(false);
  const { updatePaymentStatus, paymentStatus } = usePayment();
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const { ensureTransaction, clearTransaction } = useCreateTransaction(
    typeForUserAccessLevel || 'in_app'
  );
  const { invalidateTransaction } = useCheckoutPrewarm();

  const inFlightRef = useRef(false);
  const lastParamsRef = useRef<string | null>(null);
  const lastPriceIdRef = useRef<string | null>(null);
  const lastTransactionIdRef = useRef<string | null>(null);
  const ownerIdRef = useRef<CheckoutOwnerId | null>(null);
  if (!ownerIdRef.current) {
    ownerIdRef.current = Symbol('useCheckout');
  }

  const buildParamsKey = (params: {
    priceId: string;
    quantity: number;
    discountId?: string;
    toolType?: string;
    paywallType?: string;
    allowedPaymentMethods?: AvailablePaymentMethod[];
  }) =>
    `${params.priceId}:${params.quantity}:${params.discountId ?? ''}:${params.toolType ?? ''}:${params.paywallType ?? ''}:${params.allowedPaymentMethods?.join(',') ?? ''}`;

  const openCheckout = async ({
    priceId,
    quantity,
    discountId,
    toolType,
    paywallType,
    displayMode = 'inline',
    allowedPaymentMethods,
    frameInitialHeight = 450,
  }: {
    priceId: string;
    quantity: number;
    discountId?: string;
    toolType?: string;
    paywallType?: string;
    displayMode?: 'inline' | 'overlay';
    allowedPaymentMethods?: AvailablePaymentMethod[];
    frameInitialHeight?: number;
  }) => {
    if (!user || Object.keys(user).length === 0 || !paddle) {
      return false;
    }

    const customerAddress = getCheckoutCustomerAddress();

    const checkoutCustomer = user.customerId
      ? { id: user.customerId, ...(customerAddress && { address: customerAddress }) }
      : user.email
        ? { email: user.email, ...(customerAddress && { address: customerAddress }) }
        : undefined;

    const paramsKey = buildParamsKey({
      priceId,
      quantity,
      discountId,
      toolType,
      paywallType,
      allowedPaymentMethods,
    });

    if (isCheckoutOpen && lastParamsRef.current === paramsKey) {
      return;
    }

    if (inFlightRef.current) {
      return;
    }

    const retainedTransactionId = checkoutRevealStore.getRetainedTransactionId(
      paramsKey,
      RETAINED_TRANSACTION_TTL_MS
    );

    inFlightRef.current = true;
    if (!retainedTransactionId) {
      updatePaymentStatus('loading');
    }

    try {
      clearTransaction();
      const transactionId =
        retainedTransactionId ??
        (await ensureTransaction({
          priceId,
          discountId,
          quantity,
          toolType,
          paywallType,
        }));
      if (!transactionId) throw new Error('Missing transaction id');

      if (displayMode === 'overlay') {
        paddle.Checkout.open({
          transactionId,
          ...(checkoutCustomer ? { customer: checkoutCustomer } : {}),
          settings: {
            displayMode: 'overlay',
            variant: 'one-page',
            allowLogout: false,
            showAddDiscounts: false,
            showAddTaxId: false,
            ...(allowedPaymentMethods?.length
              ? { allowedPaymentMethods }
              : {}),
          },
        });
      } else if (
        checkoutRevealStore.getLoadedTransactionId() !== transactionId
      ) {
        paddle.Checkout.open({
          transactionId,
          ...(checkoutCustomer ? { customer: checkoutCustomer } : {}),
          settings: {
            displayMode: 'inline',
            variant: 'one-page',
            allowLogout: false,
            showAddDiscounts: false,
            showAddTaxId: false,
            frameTarget: 'checkout-container',
            frameInitialHeight,
            frameStyle:
              `width: 100%; min-width: 312px; min-height: ${frameInitialHeight}px; background-color: transparent; border: none;`,
            ...(allowedPaymentMethods?.length
              ? { allowedPaymentMethods }
              : {}),
          },
        });
        checkoutRevealStore.setLoadedTransactionId(transactionId);
      }

      checkoutVisibilityStore.setVisibleTransaction(
        transactionId,
        ownerIdRef.current as CheckoutOwnerId
      );

      if (retainedTransactionId) {
        updatePaymentStatus('checkout_ready');
      } else {
        checkoutRevealStore.markRetained(paramsKey);
      }

      lastParamsRef.current = paramsKey;
      lastPriceIdRef.current = priceId;
      lastTransactionIdRef.current = transactionId;
      setIsCheckoutOpen(true);
      return true;
    } catch (error) {
      console.error('Failed to open checkout:', error);
      updatePaymentStatus('failed');
      return false;
    } finally {
      inFlightRef.current = false;
    }
  };

  const updateCheckout = async ({
    priceId,
    quantity,
    discountId,
    toolType,
    paywallType,
  }: {
    priceId: string;
    quantity: number;
    discountId?: string;
    toolType?: string;
    paywallType?: string;
  }) => {
    if (!paddle || paymentStatus !== 'checkout_ready') return;

    const paramsKey = buildParamsKey({
      priceId,
      quantity,
      discountId,
      toolType,
      paywallType,
    });
    if (lastParamsRef.current === paramsKey) {
      return;
    }

    if (isCheckoutOpen) {
      paddle.Checkout.close();
      setIsCheckoutOpen(false);
      checkoutVisibilityStore.clearVisibleTransaction(
        lastTransactionIdRef.current ?? undefined
      );
      checkoutRevealStore.clearLoadedTransactionId(
        lastTransactionIdRef.current ?? undefined
      );
      lastTransactionIdRef.current = null;
    }

    await openCheckout({
      priceId,
      quantity,
      discountId,
      toolType,
      paywallType,
    });
  };

  const closeCheckout = () => {
    if (paddle && isCheckoutOpen) {
      setIsCheckoutOpen(false);
      clearTransaction();
      checkoutVisibilityStore.clearVisibleTransaction(
        lastTransactionIdRef.current ?? undefined
      );

      const isSpent = paymentStatus === 'completed' || paymentStatus === 'failed';

      if (isSpent) {
        paddle.Checkout.close();
        checkoutRevealStore.clearLoadedTransactionId(
          lastTransactionIdRef.current ?? undefined
        );
        if (lastPriceIdRef.current) {
          invalidateTransaction(lastPriceIdRef.current);
        }
      }

      lastParamsRef.current = null;
      lastPriceIdRef.current = null;
      lastTransactionIdRef.current = null;
    }
  };

  return {
    isCheckoutOpen,
    openCheckout,
    updateCheckout,
    closeCheckout,
  };
};

export default useCheckout;
